import { Badge } from '@/components/ui/badge';
import { 
  Sparkles, Wrench, Droplets, BedDouble, 
  CheckCircle, AlertTriangle, XCircle, User,
  Calendar, LogIn, LogOut
} from 'lucide-react';

/**
 * PmsStatusBadge - Single source of truth for PMS status styling
 * Maps status values to semantic colors, icons, and labels
 */

const STATUS_CONFIG = {
  // Room Statuses
  clean: {
    label: 'Clean',
    icon: Sparkles,
    className: 'bg-[hsl(var(--status-clean)/0.12)] text-[hsl(var(--status-clean))] border-[hsl(var(--status-clean)/0.22)]'
  },
  dirty: {
    label: 'Dirty',
    icon: Droplets,
    className: 'bg-[hsl(var(--status-dirty)/0.12)] text-[hsl(var(--status-dirty))] border-[hsl(var(--status-dirty)/0.22)]'
  },
  occupied: {
    label: 'Occupied',
    icon: BedDouble,
    className: 'bg-[hsl(var(--status-occupied)/0.12)] text-[hsl(var(--status-occupied))] border-[hsl(var(--status-occupied)/0.22)]'
  },
  vacant: {
    label: 'Vacant',
    icon: BedDouble,
    className: 'bg-[hsl(var(--status-vacant)/0.12)] text-[hsl(var(--status-vacant))] border-[hsl(var(--status-vacant)/0.22)]'
  },
  maintenance: {
    label: 'Maintenance',
    icon: Wrench,
    className: 'bg-[hsl(var(--status-maintenance)/0.12)] text-[hsl(var(--status-maintenance))] border-[hsl(var(--status-maintenance)/0.22)]'
  },
  inspected: {
    label: 'Inspected',
    icon: CheckCircle,
    className: 'bg-[hsl(var(--status-inspected)/0.12)] text-[hsl(var(--status-inspected))] border-[hsl(var(--status-inspected)/0.22)]'
  },
  out_of_order: {
    label: 'Out of Order',
    icon: XCircle,
    className: 'bg-[hsl(var(--status-out-of-order)/0.12)] text-[hsl(var(--status-out-of-order))] border-[hsl(var(--status-out-of-order)/0.22)]'
  },
  
  // Operational Statuses
  arrival: {
    label: 'Arrival',
    icon: LogIn,
    className: 'bg-[hsl(var(--status-arrival)/0.12)] text-[hsl(var(--status-arrival))] border-[hsl(var(--status-arrival)/0.22)]'
  },
  departure: {
    label: 'Departure',
    icon: LogOut,
    className: 'bg-[hsl(var(--status-departure)/0.12)] text-[hsl(var(--status-departure))] border-[hsl(var(--status-departure)/0.22)]'
  },
  vip: {
    label: 'VIP',
    icon: User,
    className: 'bg-[hsl(var(--status-vip)/0.12)] text-[hsl(var(--status-vip))] border-[hsl(var(--status-vip)/0.22)]'
  },
  
  // Housekeeping Task Statuses
  pending: {
    label: 'Pending',
    icon: Calendar,
    className: 'bg-muted/50 text-muted-foreground border-muted'
  },
  in_progress: {
    label: 'In Progress',
    icon: Wrench,
    className: 'bg-primary/10 text-primary border-primary/20'
  },
  completed: {
    label: 'Completed',
    icon: CheckCircle,
    className: 'bg-[hsl(var(--status-clean)/0.12)] text-[hsl(var(--status-clean))] border-[hsl(var(--status-clean)/0.22)]'
  }
};

export const PmsStatusBadge = ({ 
  status, 
  showIcon = true, 
  variant = 'default',
  className = '',
  size = 'default'
}) => {
  // Normalize status (convert spaces/hyphens to underscores, lowercase)
  const normalizedStatus = status?.toLowerCase().replace(/[\s-]/g, '_');
  
  const config = STATUS_CONFIG[normalizedStatus] || {
    label: status || 'Unknown',
    icon: AlertTriangle,
    className: 'bg-muted text-muted-foreground'
  };
  
  const Icon = config.icon;
  const sizeClass = size === 'sm' ? 'w-3 h-3' : 'w-4 h-4';
  
  return (
    <Badge
      variant="outline"
      className={`${config.className} ${className} rounded-full font-medium`}
      data-testid={`pms-status-badge-${normalizedStatus}`}
    >
      {showIcon && <Icon className={`${sizeClass} mr-1`} />}
      {config.label}
    </Badge>
  );
};

// Export status config for use in other components
export const getStatusConfig = (status) => {
  const normalizedStatus = status?.toLowerCase().replace(/[\s-]/g, '_');
  return STATUS_CONFIG[normalizedStatus];
};

export const ROOM_STATUSES = ['clean', 'dirty', 'occupied', 'vacant', 'maintenance', 'inspected', 'out_of_order'];
export const TASK_STATUSES = ['pending', 'in_progress', 'completed'];
